import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/3163534/3163534-uhd_2560_1440_30fps.mp4";

const Hero = () => {
  const { t } = useLang();
  const [videoOk, setVideoOk] = useState(true);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain-overlay"
      style={{ backgroundColor: "#002B6B" }}
    >
      {videoOk && (
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          src={HERO_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setVideoOk(false)}
          onStalled={() => setVideoOk(false)}
          data-testid="hero-video"
        />
      )}

      {/* Color blends */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,43,107,0.6) 0%, rgba(0,43,107,0.55) 45%, rgba(0,26,69,0.9) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay"
        style={{
          background:
            "radial-gradient(900px 600px at 85% 20%, rgba(111,45,189,0.45), transparent 60%)",
        }}
      />

      {/* Vertical tagline */}
      <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
        <span className="vertical-text text-white/55 text-[11px] tracking-[0.4em] uppercase font-medium">
          {t.hero.tagline}
        </span>
      </div>

      <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-end pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
          className="max-w-5xl"
        >
          <p
            data-testid="hero-overline"
            className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-white/75 mb-8 flex items-center gap-3 font-semibold"
          >
            <span className="inline-block w-12 h-px bg-white/60" />
            {t.hero.overline}
          </p>

          <h1
            data-testid="hero-title"
            className="font-display text-white text-[44px] md:text-7xl lg:text-[92px] leading-[0.95] tracking-[-0.025em] uppercase"
            style={{ fontWeight: 700 }}
          >
            {t.hero.title.split(" ").map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.05, duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
                className="inline-block mr-[0.25em]"
              >
                {word === "constante" || word === "evolución" || word === "constant" || word === "evolution" ? (
                  <em className="not-italic" style={{ color: "#B89DDE", fontWeight: 500 }}>{word}</em>
                ) : (
                  word
                )}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            data-testid="hero-subtitle"
            className="mt-8 md:mt-10 text-base md:text-xl text-white/80 font-light max-w-2xl leading-relaxed font-editorial"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.6 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              href="#divisions"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-white text-[#002B6B] px-7 md:px-8 py-4 text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-bold hover:bg-[#F7F8FA] transition-colors"
            >
              {t.hero.cta1}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#contact"
              data-testid="hero-cta2-button"
              className="group inline-flex items-center gap-3 border border-white/35 text-white px-7 md:px-8 py-4 text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-white/10 transition-colors"
            >
              {t.hero.cta2}
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom marker */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between text-white/60 text-[10px] tracking-[0.3em] uppercase border-t border-white/15 font-semibold">
          <span>MX · USA · LATAM</span>
          <span className="hidden md:inline">© ARIIVA — {new Date().getFullYear()}</span>
          <span>Scroll ↓</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
